/**
 * Curated IANA timezones offered in the channel admin form and accepted by
 * the server. Single source of truth shared between admin UI (renders the
 * grouped <select>) and the server (validates the submitted value).
 *
 * The list is intentionally compact — broadcasting clients we serve fall
 * within these regions. Add to this file when a new region is needed; the
 * dashboard renders the channel's wall-clock via `Intl.DateTimeFormat` with
 * the IANA `value`, so any tz in the IANA database would technically work,
 * but a closed list keeps the form predictable and prevents typo'd values
 * silently degrading to UTC.
 */
export const DEFAULT_DISPLAY_TIMEZONE = 'Europe/Paris';
export const TIMEZONE_GROUPS = [
    {
        label: 'Europe',
        options: [
            { value: 'Europe/Paris', label: 'Paris / Bruxelles / Madrid (CET/CEST)' },
            { value: 'Europe/London', label: 'Londres / Lisbonne (GMT/BST)' },
            { value: 'Europe/Athens', label: 'Athènes / Helsinki (EET/EEST)' },
        ],
    },
    {
        label: "France d'Outre-mer",
        options: [
            { value: 'America/Guadeloupe', label: 'Antilles — Guadeloupe / Martinique (UTC−4)' },
            { value: 'America/Cayenne', label: 'Guyane (UTC−3)' },
            { value: 'America/Miquelon', label: 'Saint-Pierre-et-Miquelon (UTC−3/−2)' },
            { value: 'Indian/Mayotte', label: 'Mayotte (UTC+3)' },
            { value: 'Indian/Reunion', label: 'La Réunion (UTC+4)' },
            { value: 'Pacific/Tahiti', label: 'Tahiti — Polynésie française (UTC−10)' },
            { value: 'Pacific/Noumea', label: 'Nouvelle-Calédonie (UTC+11)' },
            { value: 'Pacific/Wallis', label: 'Wallis-et-Futuna (UTC+12)' },
        ],
    },
    {
        label: 'Afrique',
        options: [
            { value: 'Africa/Casablanca', label: 'Casablanca (WET/WEST)' },
            { value: 'Africa/Algiers', label: 'Alger / Tunis (CET)' },
            { value: 'Africa/Abidjan', label: 'Abidjan / Dakar (GMT)' },
            { value: 'Africa/Lagos', label: 'Lagos / Brazzaville (WAT)' },
            { value: 'Africa/Nairobi', label: 'Nairobi (EAT)' },
            { value: 'Africa/Johannesburg', label: 'Johannesburg (SAST)' },
        ],
    },
    {
        label: 'Amériques',
        options: [
            { value: 'America/Montreal', label: 'Montréal / New York (EST/EDT)' },
            { value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
            { value: 'America/Denver', label: 'Denver (MST/MDT)' },
            { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
            { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)' },
            { value: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires (ART)' },
        ],
    },
    {
        label: 'Asie / Pacifique',
        options: [
            { value: 'Asia/Dubai', label: 'Dubaï (GST)' },
            { value: 'Asia/Kolkata', label: 'Mumbai / New Delhi (IST)' },
            { value: 'Asia/Bangkok', label: 'Bangkok / Hô Chi Minh-Ville (ICT)' },
            { value: 'Asia/Singapore', label: 'Singapour / Hong Kong (SGT/HKT)' },
            { value: 'Asia/Tokyo', label: 'Tokyo / Séoul (JST/KST)' },
            { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
            { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
        ],
    },
    {
        label: 'UTC',
        options: [{ value: 'UTC', label: 'UTC' }],
    },
];
/** Flat allowlist used for server-side validation. */
export const ALLOWED_TIMEZONES = new Set(TIMEZONE_GROUPS.flatMap((g) => g.options.map((o) => o.value)));
//# sourceMappingURL=timezones.js.map